import { z } from 'zod'
import { l } from '@manychat/manyui'

import { UpgradeSource, billing } from 'common/billing'
import { alert } from 'common/core'
import { formatEntitiesListResponse } from 'common/usesList/utils'
import { SmartSegment, SmartSegmentSchema } from 'shared/api/common/schemas/smartSegments'
import { handleError } from 'shared/api/lib/errors/handlers'
import { smartSegmentApi } from 'shared/api/requests/smartSegments'
import {
  CreateSmartSegmentRequest,
  RenameSmartSegmentRequest,
  UpdateSmartSegmentDescriptionRequest,
  UpdateSmartSegmentFilterRequest,
} from 'shared/api/requests/smartSegments/schemas'
import { createAsyncAction } from 'shared/lib/redux'
import { getPageId } from 'utils/router/tools'
import { analyticsService } from 'utils/services/analytics'
import { accountNotificationsListener } from 'utils/services/notificationsService'
import { SocketEventBaseSchema } from 'utils/services/notificationsService/constants'

import { smartSegmentsActions } from '../reducers/smartSegments'

const NAMESPACE = 'smartSegmentCurrent'

export const fetchSmartSegment = createAsyncAction(
  `${NAMESPACE}/fetchSmartSegment`,
  async (segmentId: SmartSegment['segment_id']) => {
    try {
      const response = await smartSegmentApi.fetch({
        query: { smart_segment_id: segmentId },
      })

      return response.data.segment
    } catch (error) {
      handleError(error)
    }

    return null
  },
  {
    errorMarkupOptions: {
      scope: 'audience',
      section: 'smart_segments',
    },
    autoHandleByGuards: [smartSegmentApi.fetch.isSmartSegmentNotFound],
  },
)

export const createSmartSegment = createAsyncAction(
  `${NAMESPACE}/createSmartSegment`,
  async (requestBodyParams: CreateSmartSegmentRequest) => {
    const response = await smartSegmentApi.create({ body: requestBodyParams })
    analyticsService.sendEvent('SMART.SEGMENT.CREATED')
    alert(l.translate('Segment created!'), 'success')

    return response.data.segment
  },
  {
    onBusinessError: (error) => {
      error.handle()
      if (billing.isProError(error)) {
        billing.handleProError(error, {
          message: l.translate(
            `Contact Segments is a Pro feature. Please upgrade your subscription to create and view Segments.`,
          ),
          title: l.translate(
            `Unlock the ability to create Contact Segments with Manychat Pro Subscription`,
          ),
          source: UpgradeSource.SMART_SEGMENTS,
        })
      } else {
        alert(error, 'danger')
      }
      throw error
    },
    errorMarkupOptions: {
      scope: 'audience',
      section: 'smart_segments',
    },
  },
)

export const renameSmartSegment = createAsyncAction(
  `${NAMESPACE}/renameSmartSegment`,
  async (body: RenameSmartSegmentRequest) => {
    const response = await smartSegmentApi.rename({
      body,
    })
    smartSegmentsActions.updateItem({
      id: response.data.segment.segment_id,
      data: response.data.segment,
    })
    analyticsService.sendEvent('SMART.SEGMENT.UPDATED', { updated: 'NAME' })
    alert(l.translate('Segment renamed!'), 'success')

    return response.data.segment
  },
  {
    errorMarkupOptions: {
      scope: 'audience',
      section: 'smart_segments',
    },
  },
)

export const saveSmartSegmentFilter = createAsyncAction(
  `${NAMESPACE}/saveSmartSegmentFilter`,
  async (body: UpdateSmartSegmentFilterRequest) => {
    const response = await smartSegmentApi.updateFilter({
      body,
    })
    analyticsService.sendEvent('SMART.SEGMENT.UPDATED', { updated: 'FILTER' })
    alert(l.translate('Segment updated!'), 'success')

    return response.data.segment
  },
  {
    onBusinessError: (error) => {
      error.handle()
      if (billing.isProError(error)) {
        billing.handleProError(error, {
          source: UpgradeSource.SMART_SEGMENTS,
          message: l.translate(
            "You aren't able to activate Contact Segments on the current plan. Upgrade your plan to to create and view Segments.",
          ),
          title: l.translate(
            `Unlock the ability to update Contact Segments with Manychat Pro Subscription`,
          ),
        })
      } else {
        alert(error, 'danger')
      }
      throw error
    },
    errorMarkupOptions: {
      scope: 'audience',
      section: 'smart_segments',
    },
  },
)

export const updateSmartSegmentDescription = createAsyncAction(
  `${NAMESPACE}/updateSmartSegmentDescription`,
  async (body: UpdateSmartSegmentDescriptionRequest) => {
    const response = await smartSegmentApi.updateDescription({
      body,
    })
    analyticsService.sendEvent('SMART.SEGMENT.UPDATED', { updated: 'DESCRIPTION' })
    alert(l.translate('Segment description updated!'), 'success')

    return response.data.segment
  },
  {
    errorMarkupOptions: {
      scope: 'audience',
      section: 'smart_segments',
    },
  },
)

export const getUsesList = createAsyncAction(
  `${NAMESPACE}/getUsesList`,
  async (smartSegmentId: SmartSegment['segment_id']) => {
    try {
      const pageId = getPageId()
      const response = await smartSegmentApi.getUsesList({
        query: { smart_segment_id: smartSegmentId },
      })

      return formatEntitiesListResponse(response.data, pageId)
    } catch (error) {
      alert(l.translate('Uses checking failed'), 'danger')
      throw error
    }
  },
  {
    errorMarkupOptions: {
      scope: 'audience',
      section: 'smart_segments',
    },
  },
)

const SmartSegmentCreatedEvent = SocketEventBaseSchema.extend({
  event: z.literal('smart_segment_created'),
  model: SmartSegmentSchema,
})

accountNotificationsListener.on(SmartSegmentCreatedEvent, (data, dispatch) => {
  dispatch(smartSegmentsActions.appendItem({ item: data.model }))
})

const SmartSegmentUpdatedEvent = SocketEventBaseSchema.extend({
  event: z.literal('smart_segment_updated'),
  model: SmartSegmentSchema,
})

accountNotificationsListener.on(SmartSegmentUpdatedEvent, (data, dispatch) => {
  dispatch(smartSegmentsActions.updateItem({ id: Number(data.model.id), data: data.model }))
})
